import { useCallback } from 'react'
import { AttachmentPromiseClient } from '@trustero/trustero-api-web/lib/attachment/attachment_grpc_web_pb'
import { ModelStatsServicePromiseClient } from '@trustero/trustero-api-web/lib/model/stats_grpc_web_pb'
import { ListControlComplianceStatsRequest } from '@trustero/trustero-api-web/lib/model/stats_pb'
import { ModelPromiseClient } from '@trustero/trustero-api-web/lib/model/model_grpc_web_pb'
import { EvidenceManagementPromiseClient } from '@trustero/trustero-api-web/lib/evidencemgmt/evidencemgmt_grpc_web_pb'
import { RequestPromiseClient } from '@trustero/trustero-api-web/lib/request/request_grpc_web_pb'
import { useInvalidateControlTestStaleness } from '../../components/async/model/control/useControlTestStaleness'
import {
  useGrpcRevalidateByMethod,
  useGrpcRevalidateByMethodRequest,
} from '../../components'

/**
 * ! Should be used every time Evidence is added or deleted from a Control
 *
 * Returns a function that invalidates all cache keys affiliated with
 * the fetching of evidence list and evidence count for the given control
 *
 * This then triggers a refetch of a Control's evidence list and evidence count
 * @returns Returns a function to invalidate cache keys
 */
export const useInvalidateEvidence = ({
  includeMappings = true,
}: { includeMappings?: boolean } = {}): (() => Promise<void>) => {
  const grpcRevalidateByMethodRequest = useGrpcRevalidateByMethodRequest()
  const grpcRevalidateByMethod = useGrpcRevalidateByMethod()
  // Evidence linked to a control feeds its evidence hash, so adding or removing any is
  // enough to move that control's latest test between current and outdated.
  const invalidateStaleness = useInvalidateControlTestStaleness()
  const invalidator = useCallback(async () => {
    await Promise.all([
      invalidateStaleness(),
      grpcRevalidateByMethod(
        AttachmentPromiseClient.prototype.getLatestEvidence,
      ),
      grpcRevalidateByMethod(AttachmentPromiseClient.prototype.listEvidence),
      grpcRevalidateByMethod(
        AttachmentPromiseClient.prototype.getLatestEvidenceGroups,
      ),
      grpcRevalidateByMethod(
        AttachmentPromiseClient.prototype.listEvidenceGroups,
      ),
      grpcRevalidateByMethod(
        AttachmentPromiseClient.prototype.getDocumentByOID,
      ),
      grpcRevalidateByMethod(
        AttachmentPromiseClient.prototype.getDocumentsByOIDs,
      ),
      grpcRevalidateByMethod(
        AttachmentPromiseClient.prototype.getControlEvidence,
      ),
      grpcRevalidateByMethod(
        AttachmentPromiseClient.prototype.getEvidenceControls,
      ),
      grpcRevalidateByMethod(
        AttachmentPromiseClient.prototype.getEvidenceInstanceList,
      ),
      ...(includeMappings
        ? [
            grpcRevalidateByMethod(
              EvidenceManagementPromiseClient.prototype
                .getControlToEvidenceMappingInfo,
            ),
            grpcRevalidateByMethod(
              EvidenceManagementPromiseClient.prototype
                .getEvidenceToControlMappingInfo,
            ),
            grpcRevalidateByMethod(
              EvidenceManagementPromiseClient.prototype
                .getControlToEvidenceMappings,
            ),
            grpcRevalidateByMethod(
              EvidenceManagementPromiseClient.prototype
                .getEvidenceToControlMappings,
            ),
          ]
        : []),
      grpcRevalidateByMethod(
        RequestPromiseClient.prototype.listDocumentRequests,
      ),
      grpcRevalidateByMethod(
        RequestPromiseClient.prototype.listRequestComments,
      ),
      grpcRevalidateByMethodRequest(
        ModelStatsServicePromiseClient.prototype.listControlComplianceStats,
        new ListControlComplianceStatsRequest(),
      ),
      grpcRevalidateByMethod(
        AttachmentPromiseClient.prototype.getControlEvidenceTabView,
      ),
      grpcRevalidateByMethod(
        AttachmentPromiseClient.prototype.getRequestEvidenceTabView,
      ),
      grpcRevalidateByMethod(
        AttachmentPromiseClient.prototype.getEvidenceIndexPageView,
      ),
      grpcRevalidateByMethod(ModelPromiseClient.prototype.listControls),
      grpcRevalidateByMethod(
        AttachmentPromiseClient.prototype.getEvidenceGroupById,
      ),
    ])
  }, [
    grpcRevalidateByMethodRequest,
    grpcRevalidateByMethod,
    invalidateStaleness,
    includeMappings,
  ])

  return invalidator
}

// Keep older callers on the same invalidation set as current evidence views.
export const useHardEvidenceInvalidation = (): (() => Promise<void>) => {
  return useInvalidateEvidence()
}
